import { Module } from 'vuex';
import { RootState, DictState, DictionaryFieldsWithGroup } from '../types';
import { api } from '../../plugins/salut-api';
import {
	ContactTypeCode,
	ContactPresetField,
	ContactDictionary,
	ProfileType,
	FamilyApplicantType,
	DictionaryFieldProperties,
	FormFields,
} from '../../types';
import uniqBy from 'lodash/uniqBy';

const SET_CONTACT_DICT = 'SET_CONTACT_DICT';
const SET_MEMBER_DICT = 'SET_MEMBER_DICT';
const SET_VOLUNTEER_DICT = 'SET_VOLUNTEER_DICT';
const SET_DONOR_DICT = 'SET_DONOR_DICT';
const SET_BENEFICIARY_DICT = 'SET_BENEFICIARY_DICT';
const SET_PARTICIPANT_DICT = 'SET_PARTICIPANT_DICT';
const SET_JOB_PARTICIPANT_DICT = 'SET_JOB_PARTICIPANT_DICT';
const SET_PARTNER_DICT = 'SET_PARTNER_DICT';
const SET_CASE_DICT = 'SET_CASE_DICT';

export const DictActionKeys = {
	fetch: 'fetch',
};

export const DictGetterKeys = {
	getProfileDict: 'getProfileDict',
	getContactDict: 'getContactDict',
	getContactDictionaryFields: 'getContactDictionaryFields',
	contactDictFieldsWithoutGroup: 'contactDictFieldsWithoutGroup',
	contactDictFieldsWithGroup: 'contactDictFieldsWithGroup',
	getAllProfileDictionaryFieldsByContactType:
		'getAllProfileDictionaryFieldsByContactType',
	getProfileDictFieldsWithGroup: 'getProfileDictFieldsWithGroup',
	getProfileDictFieldsWithoutGroup: 'getProfileDictFieldsWithoutGroup',
	contactDictionaryFieldsAsPresets: 'contactDictionaryFieldsAsPresets',
	getProfileDictionaryFieldsAsPresets: 'getProfileDictionaryFieldsAsPresets',
	getFilteredContactDictionaryFieldsAsPresets:
		'getFilteredContactDictionaryFieldsAsPresets',
	getFilteredProfileDictionaryFieldsAsPresets:
		'getFilteredProfileDictionaryFieldsAsPresets',
};

function recursivelyUnpackDictFieldsToFormFields(
	fieldKey: string,
	dictField: DictionaryFieldProperties,
	formFields: FormFields,
) {
	const { children, ...formField } = dictField;
	if (children) {
		for (const child of children) {
			if (!child.mapId) {
				console.error('Dict field child missing mapId!', child);
				continue;
			}
			formFields = recursivelyUnpackDictFieldsToFormFields(
				child.mapId,
				child,
				formFields,
			);
		}
	}
	formFields[fieldKey] = formField;
	return formFields;
}

function convertDictFieldToFormFields(
	fieldKey: string,
	field: DictionaryFieldProperties,
): FormFields {
	const fields: FormFields = recursivelyUnpackDictFieldsToFormFields(
		fieldKey,
		field,
		{},
	);
	return fields;
}

const store: Module<DictState, RootState> = {
	namespaced: true,
	state: {
		profileDictionaries: {
			member: [],
			volunteer: [],
			donor: [],
			beneficiary: [],
			participant: [],
			jobParticipant: [],
			partner: [],
			case: [],
		},
		contactDictionaries: [],
	},
	mutations: {
		[SET_CONTACT_DICT](state, data) {
			state.contactDictionaries = data;
		},
		[SET_MEMBER_DICT](state, data) {
			state.profileDictionaries.member = data;
		},
		[SET_VOLUNTEER_DICT](state, data) {
			state.profileDictionaries.volunteer = data;
		},
		[SET_DONOR_DICT](state, data) {
			state.profileDictionaries.donor = data;
		},
		[SET_BENEFICIARY_DICT](state, data) {
			state.profileDictionaries.beneficiary = data;
		},
		[SET_PARTICIPANT_DICT](state, data) {
			state.profileDictionaries.participant = data;
		},
		[SET_JOB_PARTICIPANT_DICT](state, data) {
			state.profileDictionaries.jobParticipant = data;
		},
		[SET_PARTNER_DICT](state, data) {
			state.profileDictionaries.partner = data;
		},
		[SET_CASE_DICT](state, data) {
			state.profileDictionaries.partner = data;
		},
	},
	actions: {
		async [DictActionKeys.fetch](
			{ commit, getters },
			params?: { contactType?: ContactTypeCode },
		) {
			const { contactType } = params || {};
			if (contactType) {
				if (
					getters[DictGetterKeys.getAllProfileDictionaryFieldsByContactType]({
						contactType,
						isForm: true,
					}).length > 0
				) {
					// skip. No need to load again
					return;
				}
				const data = await api.fetchContactDictionary(contactType);
				commit(
					{
						member: SET_MEMBER_DICT,
						volunteer: SET_VOLUNTEER_DICT,
						donor: SET_DONOR_DICT,
						beneficiary: SET_BENEFICIARY_DICT,
						participant: SET_PARTICIPANT_DICT,
						jobParticipant: SET_JOB_PARTICIPANT_DICT,
						partner: SET_PARTNER_DICT,
						case: SET_CASE_DICT,
					}[contactType],
					data,
				);
			} else {
				const data = await api.fetchContactDictionary();
				commit(SET_CONTACT_DICT, data);
			}
		},
	},
	getters: {
		[DictGetterKeys.getProfileDict]: state => ({
			isForm,
			contactType,
			profileType,
			familyApplicantTypes,
		}: {
			isForm: boolean;
			contactType: ContactTypeCode;
			profileType?: ProfileType;
			familyApplicantTypes?: FamilyApplicantType[];
		}) =>
			state.profileDictionaries[contactType]
				?.map(dict => ({
					...dict,
					fields: dict.fields.filter(field => field.mapId),
				}))
				.filter(
					dict =>
						(isForm || !dict.formOnly) &&
						(!profileType ||
							!dict.profileType?.id ||
							dict.profileType?.id === profileType.id) &&
						dict.fields.length,
				)
				.filter(
					dict =>
						(familyApplicantTypes?.length
							? dict.familyApplicantType
								? familyApplicantTypes.includes(dict.familyApplicantType)
								: true
							: !dict.familyApplicantType) && dict.fields.length,
				) || [],
		[DictGetterKeys.getContactDict]: state => (options?: {
			isForm?: boolean;
		}) =>
			state.contactDictionaries.filter(
				dict => options?.isForm || !dict.formOnly,
			),
		[DictGetterKeys.getContactDictionaryFields]: (_, getters) => (options?: {
			isForm?: boolean;
		}): DictionaryFieldProperties[] => {
			return ([] as DictionaryFieldProperties[]).concat(
				...(getters[DictGetterKeys.getContactDict](
					options,
				) as ContactDictionary[]).map((dict: ContactDictionary) => dict.fields),
			);
		},
		[DictGetterKeys.contactDictFieldsWithoutGroup]: (
			_,
			getters,
		): DictionaryFieldProperties[] => {
			return ([] as DictionaryFieldProperties[]).concat(
				...(getters[DictGetterKeys.getContactDict]() as ContactDictionary[])
					.filter(dict => !dict.fieldGroup?.name?.en)
					.map(dict => dict.fields),
			);
		},
		[DictGetterKeys.contactDictFieldsWithGroup]: (
			_,
			getters,
		): DictionaryFieldsWithGroup[] => {
			const arrayOfFieldsWithGroup: DictionaryFieldsWithGroup[] = [];
			for (const dictionary of getters[
				DictGetterKeys.getContactDict
			]() as ContactDictionary[]) {
				const groupName = dictionary.fieldGroup?.name;
				const groupEnglishName = groupName?.en;
				if (!groupEnglishName) {
					continue;
				}
				let groupExists = false;
				for (const fieldsWithGroup of arrayOfFieldsWithGroup) {
					if (fieldsWithGroup.groupName?.en === groupEnglishName) {
						// add to existing group
						groupExists = true;
						fieldsWithGroup.fields.push(...dictionary.fields);
					}
				}
				if (!groupExists) {
					arrayOfFieldsWithGroup.push({
						fields: dictionary.fields,
						groupName: groupName,
					});
				}
			}
			return arrayOfFieldsWithGroup;
		},
		[DictGetterKeys.getAllProfileDictionaryFieldsByContactType]: (
			_,
			getters,
		) => ({
			contactType,
			isForm,
		}: {
			contactType: ContactTypeCode;
			isForm?: boolean;
		}): DictionaryFieldProperties[] => {
			const fields: DictionaryFieldProperties[] = ([] as DictionaryFieldProperties[]).concat(
				...(getters[DictGetterKeys.getProfileDict]({
					isForm,
					contactType,
					familyApplicantTypes: [
						FamilyApplicantType.main,
						FamilyApplicantType.dependant,
					],
				}) as ContactDictionary[]).map(dict => dict.fields),
			);
			return uniqBy(fields, field => field.mapId);
		},
		[DictGetterKeys.getProfileDictFieldsWithoutGroup]: (_, getters) => (
			contactType: ContactTypeCode,
			profileType?: ProfileType,
			familyApplicantType?: FamilyApplicantType,
		): DictionaryFieldProperties[] => {
			let grouplessFields: DictionaryFieldProperties[] = [];
			for (const dictionary of getters[DictGetterKeys.getProfileDict]({
				contactType,
				profileType,
				familyApplicantTypes: familyApplicantType ? [familyApplicantType] : [],
			}) as ContactDictionary[]) {
				if (!dictionary.fieldGroup) {
					grouplessFields = grouplessFields.concat(dictionary.fields);
					continue;
				}
			}
			return grouplessFields;
		},
		[DictGetterKeys.getProfileDictFieldsWithGroup]: (_, getters) => (
			contactType: ContactTypeCode,
			profileType: ProfileType,
			familyApplicantType?: FamilyApplicantType,
		): DictionaryFieldsWithGroup[] => {
			const arrayOfFieldsWithGroup: DictionaryFieldsWithGroup[] = [];
			for (const dictionary of getters[DictGetterKeys.getProfileDict]({
				contactType,
				profileType,
				familyApplicantTypes: familyApplicantType ? [familyApplicantType] : [],
			}) as ContactDictionary[]) {
				const groupName = dictionary.fieldGroup?.name;
				const groupEnglishName = groupName?.en;
				if (!groupEnglishName) {
					continue;
				}
				const fields = dictionary.fields;
				let groupExists = false;
				for (const fieldsWithGroup of arrayOfFieldsWithGroup) {
					if (fieldsWithGroup.groupName?.en === groupEnglishName) {
						// add to existing group
						groupExists = true;
						fieldsWithGroup.fields = fieldsWithGroup.fields.concat(fields);
					}
				}
				if (!groupExists) {
					arrayOfFieldsWithGroup.push({
						fields: fields,
						groupName: groupName,
					});
				}
			}
			return arrayOfFieldsWithGroup;
		},
		[DictGetterKeys.contactDictionaryFieldsAsPresets]: (
			_,
			getters,
		): ContactPresetField[] =>
			getters[DictGetterKeys.getContactDictionaryFields]({ isForm: true }).map(
				(field: DictionaryFieldProperties): ContactPresetField => {
					const fieldKey = field.mapId || 'default_field_key';
					return {
						names: field.desc || { en: fieldKey },
						displayKeys: [fieldKey],
						fields: convertDictFieldToFormFields(fieldKey, field),
					};
				},
			),
		[DictGetterKeys.getProfileDictionaryFieldsAsPresets]: (_, getters) => (
			contactType: ContactTypeCode,
		): ContactPresetField[] =>
			getters[DictGetterKeys.getAllProfileDictionaryFieldsByContactType]({
				contactType,
				isForm: true,
			}).map(
				(field: DictionaryFieldProperties): ContactPresetField => {
					const fieldKey = field.mapId || 'default_field_key';
					return {
						names: field.desc || { en: fieldKey },
						displayKeys: [fieldKey],
						fields: convertDictFieldToFormFields(fieldKey, field),
					};
				},
			),
		[DictGetterKeys.getFilteredContactDictionaryFieldsAsPresets]: (
			_,
			getters,
		) => (search: string): ContactPresetField[] =>
			getters[DictGetterKeys.contactDictionaryFieldsAsPresets].filter(
				(field: ContactPresetField) =>
					Object.values(field.names)
						.filter(name => !!name)
						.map(name => name as string)
						.map(name => name.toLowerCase())
						.some(name => name.includes(search.toLowerCase())),
			),
		[DictGetterKeys.getFilteredProfileDictionaryFieldsAsPresets]: (
			_,
			getters,
		) => (contactType: ContactTypeCode, search: string): ContactPresetField[] =>
			getters[DictGetterKeys.getProfileDictionaryFieldsAsPresets](
				contactType,
			).filter((field: ContactPresetField) =>
				Object.values(field.names)
					.filter(name => !!name)
					.map(name => name as string)
					.map(name => name.toLowerCase())
					.some(name => name.includes(search.toLowerCase())),
			),
	},
};
export default store;
